// core
import { Component, OnInit, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';

// services
import { RaptorDataService } from '../../../services/raptor-data.service';
import { RaptorStepService } from '../../../services/raptor-step.service';
import { RaptorRepeaterService } from '../../../services/raptor-repeater.service';
import { RaptorPaymentService } from '../../../services/raptor-payment.service';

@Component({
  selector: 'open-raptor-page-default',
  templateUrl: './raptor-page-default.component.html',
  styleUrls: ['./raptor-page-default.component.scss']
})
export class RaptorPageDefaultComponent implements OnInit {
  @Input() formData: any;
  @Input() form!: FormGroup;

  layoutSettings: any = {};
  cardLeadOff!: boolean;
  bannerHeaders?: any;
  bannerFooters?: any;
  disclaimers?: Array<any>;
  includedExtrasDisclaimers: Array<any> = [];
  pageClass: string = '';
  layoutStructure: any = {};

  constructor(
    public raptorStepService: RaptorStepService,
    public raptorRepeaterService: RaptorRepeaterService,
    public raptorPaymentService: RaptorPaymentService,
    private raptorDataService: RaptorDataService
  ) { }

  ngOnInit(): void {
    this.setLayout(this.formData.settings);
    this.getBanners(this.formData);
  }

  ngOnChanges(changes: any) {
    if (changes.formData && changes.formData.currentValue) {
      this.setLayout(changes.formData.currentValue.settings);
    }
    this.raptorRepeaterService.repeatersValid(this.formData, this.form);
    this.getBanners(changes.formData.currentValue);
  }

  setLayout(settings: any) {
    this.pageClass = '';
    this.layoutSettings = settings || {};
    
    if (this.formData.sidebar) {
      this.layoutSettings.sidebar = this.formData.sidebar;
    }

    if (this.layoutSettings.spacing) {
      this.pageClass = `spacing-${this.layoutSettings.spacing}`;
    }
    this.layoutStructure = {
      'bg-block radius-block': this.layoutSettings.card === true,
      'cell-large-col-span-12': this.layoutSettings.size !== 'small' && this.layoutSettings.size !== 'medium',
      'cell-large-col-span-8 cell-medium-col-span-6': this.layoutSettings.size === 'medium',
      'cell-large-col-start-3 cell-medium-col-start-2': this.layoutSettings.size === 'medium' && (!this.layoutSettings.align || this.layoutSettings.align === 'center'),
      'cell-large-col-start-5 cell-medium-col-start-3': this.layoutSettings.size === 'medium' && this.layoutSettings.align === 'right',
      'cell-large-col-span-6 cell-medium-col-span-4': this.layoutSettings.size === 'small',
      'cell-large-col-start-4 cell-medium-col-start-3': this.layoutSettings.size === 'small' && (!this.layoutSettings.align || this.layoutSettings.align === 'center'),
      'cell-large-col-start-1': this.layoutSettings.size === 'small' && this.layoutSettings.align === 'left',
      'cell-large-col-start-7 cell-medium-col-start-5': this.layoutSettings.size === 'small' && this.layoutSettings.align === 'right'
    };
  }

  getBanners(formData: any) {
    // empty the object first!!!!
    this.disclaimers = [];
    if (formData.covers && Array.isArray(formData.covers)) {
      const selectedCover = this.raptorDataService.getSelectedCover(formData, this.form);
      const frequency = this.form.value.frequency || formData.data.frequency;
      this.disclaimers = this.raptorDataService.mapBannerDisclaimers([selectedCover]);

      // check for Additional charges which has disclaimers showing in the sidebar, eg. Arrangement and Service Fee
      if (selectedCover && this.formData.sidebar && frequency) {
        this.includedExtrasDisclaimers = selectedCover && this.raptorDataService.getIncludedExtrasDisclaimers(selectedCover.premium[frequency].extras);
      }
    } else if (formData.covers && typeof formData.covers === 'object') {
      this.disclaimers = this.raptorDataService.mapBannerDisclaimers(formData.covers);
      this.includedExtrasDisclaimers = [];
      const extrasDisclaimers = formData.covers.disclaimers.extras_disclaimers;
      for (const key in extrasDisclaimers) {
        if (formData.data.extras && formData.data.extras[key] && extrasDisclaimers[key]) { // Only add disclaimer for extra if extra is true and disclaimer is present
          this.includedExtrasDisclaimers.push(extrasDisclaimers[key]);
        }
      }
    }

    if (this.formData.banners?.length) {
      this.bannerHeaders = this.formData.banners.filter((banner: any) => banner.type === 'header');
      this.bannerFooters = this.formData.banners.filter((banner: any) => banner.type === 'footer');
      this.bannerHeaders.forEach((banner: any) => this.disclaimers?.push({ type: 'banner', content: banner.disclaimer }));
      this.bannerFooters.forEach((banner: any) => this.disclaimers?.push({ type: 'banner', content: banner.disclaimer }));
    }
  }

  jump(action: any) {
    this.raptorStepService.jump(action.url);
  }

  getErrorMessages(messages: any): any[] {
    return [...messages];
  }

  get endsWithTiles() {
    const lastSectionElements = this.formData.sections &&
      this.formData.sections.length &&
      this.formData.sections[this.formData.sections.length - 1] &&
      this.formData.sections[this.formData.sections.length - 1].elements;
    const lastQuestionLayout = lastSectionElements &&
    lastSectionElements[lastSectionElements.length - 1] &&
      lastSectionElements[lastSectionElements.length - 1].layout;
    let tileLast = false;

    if (lastQuestionLayout && (lastQuestionLayout === 'radio_tile' || lastQuestionLayout === 'checkbox_tile')) {
      tileLast = true;
    }

    return tileLast;
  }

  get submitDisabled() {
    const formInvalid = this.raptorStepService.submitted && !this.form.valid;
    const formLoading = this.raptorStepService.loading;
    const repeaterInvalid = this.raptorStepService.editingRepeater || !this.raptorRepeaterService.areRepeatersValid;
    const piiDisabled = this.formData.data && this.formData.data._flow_metadata && this.formData.data._flow_metadata.pii_retries === false;
    const ctaDisabled = this.formData.settings && this.formData.settings.disable_cta;
    
    return formInvalid || formLoading || this.raptorPaymentService.paying || repeaterInvalid || piiDisabled || ctaDisabled;
  }

  get submitLabel() {
    return this.raptorPaymentService.submitLabel || 
      (this.formData.settings && this.formData.settings.submit_label ? this.formData.settings.submit_label : 'Continue');
  }
}
